import { enrichIncidentPotentialRisks } from "../incidentRisk";

// The fields of an incident report that these read.
type IncidentRecord = {
	incident_type?: unknown;
	primary_consequence?: unknown;
	consequences?: unknown;
	potential_risks?: unknown;
	// The companies involved, by the names the report's lookup found.
	companies_names?: unknown;
};

// The incident types the report form offers (utils/CRUDA/incident_report.tsx).
const INCIDENT_TYPES = [
	{ value: "condition", label: "Condition" },
	{ value: "incident", label: "Incident" },
	{ value: "near_miss", label: "Near miss" },
];

// Whether a stored incident type is one the report form offers.
export const isIncidentType = (type: unknown): type is string =>
	INCIDENT_TYPES.some((option) => option.value === type);

// How the report form names a stored incident type; a dash for any other.
export const getIncidentTypeLabel = (type: unknown) =>
	INCIDENT_TYPES.find((option) => option.value === type)?.label ?? "—";

// The companies involved as one line, or nothing when there are none.
// Companies the lookup couldn't name are left out.
export function getCompanyNames(names: unknown): string | undefined {
	const list: unknown[] = Array.isArray(names) ? names : [names];
	const text = list
		.filter(
			(name): name is string => typeof name === "string" && name.trim() !== ""
		)
		.join(", ");
	return text === "" ? undefined : text;
}

// Whether the page shows an incident's consequences. The report form asks for
// them only for the Incident type; product-template doesn't require a type,
// so a report saved without one shows any it has.
export function showsIncidentConsequences(report: IncidentRecord): boolean {
	if (report.incident_type) return report.incident_type === "incident";
	const { primary_consequence: primary, consequences } = report;
	return !!primary || (Array.isArray(consequences) && consequences.length > 0);
}

// The report as the incident PDF reads it: each potential risk with its score
// (`risk`, as the Reporting Hub's export gives it) and the companies' names
// (`companies_involved`).
export function getIncidentExportData<T extends IncidentRecord>(report: T) {
	const risks = report.potential_risks;
	return {
		...report,
		potential_risks: enrichIncidentPotentialRisks(
			Array.isArray(risks) ? risks : undefined
		),
		companies_involved: getCompanyNames(report.companies_names),
	};
}

// The report with each cause's category names, for the causes table of the
// incident PDF: from the tenant's MTO categories, leaving out ids without a
// category. Without a report, nothing.
export function addCauseCategoryNames<T extends { causes?: unknown }>(
	report: T,
	categories: unknown
) {
	if (!report) return report;
	const names = new Map<string, string>();
	for (const category of Array.isArray(categories) ? categories : []) {
		if (category?._id) names.set(category._id, category.name);
	}
	const namesOf = (ids: unknown) =>
		Array.isArray(ids) ? ids.map((id) => names.get(id)).filter(Boolean) : [];
	const causes = Array.isArray(report.causes) ? report.causes : [];
	return {
		...report,
		causes: causes.map((cause: any) => ({
			...cause,
			human_category_name: namesOf(cause?.human),
			technical_category_name: namesOf(cause?.technical),
			organisational_category_name: namesOf(cause?.organisational),
		})),
	};
}
